import type { JSX } from "react";
import dayjs from "dayjs";
import { useTranslation } from "react-i18next";
import type { TFunction } from "i18next";
import type {
  EducationDetailDto,
  EnrollmentDto,
  ExamAttemptDto,
} from "../../types/client-education-detail.types";
import { EnrollmentStatus } from "../../types/client-education-detail.types";
import sectionStyles from "./sections.module.scss";

function attemptResultChip(a: ExamAttemptDto, t: TFunction) {
  if (a.status === 1) {
    return (
      <span
        className={`${sectionStyles.moduleChip} ${sectionStyles.moduleChipSuccess}`}
      >
        ✓ {t("examTags.passed")}
      </span>
    );
  }
  if (a.status === 2) {
    return (
      <span
        className={`${sectionStyles.moduleChip} ${sectionStyles.moduleChipDanger}`}
      >
        ✗ {t("examTags.failed")}
      </span>
    );
  }
  return (
    <span
      className={`${sectionStyles.moduleChip} ${sectionStyles.moduleChipWarning}`}
    >
      {t("examSection.notFinished")}
    </span>
  );
}

export function ExamSection(props: {
  education: EducationDetailDto;
  enrollment: EnrollmentDto;
  attempts: ExamAttemptDto[];
  onStart: () => void;
}) {
  const { t } = useTranslation();
  const { education, enrollment, attempts, onStart } = props;
  if (!education.exam?.id) return null;

  const startDate = education.startDate ? dayjs(education.startDate) : null;
  const notStartedYet =
    !!startDate?.isValid() && startDate.toDate().getTime() > Date.now();

  const latestPassed = attempts.find((a) => a.status === 1) ?? null;
  const latestFailed = [...attempts].reverse().find((a) => a.status === 2) ?? null;
  const inProgress = enrollment.status === EnrollmentStatus.EXAM_STARTED;

  const canStart =
    !notStartedYet &&
    !latestPassed &&
    (enrollment.status === EnrollmentStatus.CONTENT_COMPLETED ||
      enrollment.status === EnrollmentStatus.EXAM_FAILED ||
      inProgress);

  const buttonLabel = inProgress
    ? t("examSection.resumeExam")
    : latestFailed
      ? t("examSection.retryExam")
      : t("examSection.startExam");

  // Üst görselin tonu durum bağlamına göre değişir.
  const visualTone: "exam" | "passed" | "failed" | "locked" = latestPassed
    ? "passed"
    : latestFailed && !canStart
      ? "failed"
      : notStartedYet
        ? "locked"
        : "exam";

  const visualIcon = latestPassed
    ? "🏅"
    : latestFailed && !canStart
      ? "📋"
      : notStartedYet
        ? "🔒"
        : "📝";

  let statusBanner: JSX.Element | null = null;
  if (latestPassed) {
    statusBanner = (
      <div className={sectionStyles.statusBanner} data-tone="success">
        <span className={sectionStyles.statusBannerIcon} aria-hidden>
          ✓
        </span>
        <span>{t("examSection.youPassed")}</span>
      </div>
    );
  } else if (latestFailed && !canStart && !inProgress) {
    statusBanner = (
      <div className={sectionStyles.statusBanner} data-tone="danger">
        <span className={sectionStyles.statusBannerIcon} aria-hidden>
          ✗
        </span>
        <span>{t("examSection.youFailed")}</span>
      </div>
    );
  } else if (notStartedYet) {
    statusBanner = (
      <div className={sectionStyles.statusBanner} data-tone="info">
        <span className={sectionStyles.statusBannerIcon} aria-hidden>
          ⏳
        </span>
        <span>
          {t("examSection.opensAt", {
            date: startDate!.format("DD MMM YYYY"),
          })}
        </span>
      </div>
    );
  } else if (
    enrollment.status !== EnrollmentStatus.CONTENT_COMPLETED &&
    enrollment.status !== EnrollmentStatus.EXAM_FAILED &&
    !inProgress
  ) {
    statusBanner = (
      <div className={sectionStyles.statusBanner} data-tone="warning">
        <span className={sectionStyles.statusBannerIcon} aria-hidden>
          ℹ
        </span>
        <span>{t("examSection.mustCompleteContent")}</span>
      </div>
    );
  }

  return (
    <section id="exam" className={sectionStyles.section}>
      <div className={sectionStyles.header}>
        <h2>{t("examSection.heading")}</h2>
      </div>

      <div className={sectionStyles.moduleCard}>
        <div className={sectionStyles.moduleVisual} data-tone={visualTone} aria-hidden>
          <span className={sectionStyles.moduleVisualIcon}>{visualIcon}</span>
        </div>
        <div className={sectionStyles.moduleBody}>
          <div className={sectionStyles.moduleTitleRow}>
            <span className={sectionStyles.moduleName}>
              {education.exam.name ?? t("examSection.evaluation")}
            </span>
            {education.exam.durationMinutes != null && (
              <span className={sectionStyles.moduleChip}>
                ⏱{" "}
                {t("examSection.durationValue", {
                  minutes: education.exam.durationMinutes,
                })}
              </span>
            )}
          </div>

          {statusBanner}

          {attempts.length > 0 && (
            <div>
              <div className={sectionStyles.attemptListTitle}>
                {t("examSection.attemptHistory")}
              </div>
              <ul className={sectionStyles.attemptList}>
                {attempts.map((a, i) => (
                  <li key={a.id} className={sectionStyles.attemptItem}>
                    <span className={sectionStyles.attemptNumber}>
                      {t("examSection.attemptNumber", {
                        number: attempts.length - i,
                      })}
                    </span>
                    {a.startedAt && (
                      <span className={sectionStyles.attemptDate}>
                        {dayjs(a.startedAt).format("DD MMM YYYY HH:mm")}
                      </span>
                    )}
                    <span className={sectionStyles.attemptResult}>
                      {attemptResultChip(a, t)}
                    </span>
                  </li>
                ))}
              </ul>
            </div>
          )}

          {canStart && (
            <div className={sectionStyles.moduleActions}>
              <button
                type="button"
                onClick={onStart}
                className={
                  inProgress
                    ? sectionStyles.moduleActionDanger
                    : sectionStyles.moduleActionPrimary
                }
              >
                {buttonLabel}
              </button>
            </div>
          )}
        </div>
      </div>
    </section>
  );
}
